import {
  Box,
  Chip,
  CircularProgress,
  Menu,
  MenuItem,
  Tooltip,
  Typography
} from '@mui/material'
import React, { useState } from 'react'
import { useNavigate } from 'react-router-dom'
import {
  aliasDisplayEmail,
  isAliasDisabled,
  type Alias as AliasData,
  type AliasConflict
} from '../../api/types'
import {
  AliasMenuIcon,
  DisabledAliasIcon,
  EnableAliasIcon,
  PermanentDeleteIcon,
  RescheduleIcon
} from '../../icons'
import { AliasMove } from '../../paths'
import { Address } from '../address'
import { AliasEdit } from '../aliasEdit'

interface ConflictAliasProps {
  conflict: AliasConflict
  search: string
  deletingAlias?: string
  onResolveConflict: (alias: string, enable: boolean) => void
}

export const ConflictAlias: React.FC<ConflictAliasProps> = ({
  conflict,
  search,
  deletingAlias,
  onResolveConflict
}) => {
  const isDeletingEnabled = deletingAlias === conflict.enabled
  const isDeletingDisabled = deletingAlias === conflict.disabled
  const isDeleting = isDeletingEnabled || isDeletingDisabled
  const displayEmail = aliasDisplayEmail(conflict.enabled)

  return (
    <Chip
      label={
        <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
          <Address value={displayEmail} search={search} showDomain={false} />
          {!isDeleting && (
            <>
              <Box
                component="span"
                role="button"
                title="Enable"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  onResolveConflict(conflict.enabled, true)
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'error.main',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 }
                }}
              >
                <EnableAliasIcon sx={{ fontSize: '0.85rem' }} />
              </Box>
              <Box
                component="span"
                role="button"
                title="Disable"
                onClick={(e: React.MouseEvent) => {
                  e.stopPropagation()
                  onResolveConflict(conflict.enabled, false)
                }}
                sx={{
                  display: 'inline-flex',
                  alignItems: 'center',
                  color: 'error.main',
                  cursor: 'pointer',
                  '&:hover': { opacity: 0.7 }
                }}
              >
                <DisabledAliasIcon sx={{ fontSize: '0.85rem' }} />
              </Box>
            </>
          )}
        </Box>
      }
      size="small"
      variant="outlined"
      onDelete={isDeleting ? () => {} : undefined}
      deleteIcon={<CircularProgress size={12} color="inherit" />}
      sx={{
        cursor: 'default',
        color: isDeleting ? 'text.disabled' : 'error.main',
        borderColor: isDeleting ? 'divider' : 'error.main',
        textDecoration: isDeleting ? 'line-through' : 'none'
      }}
    />
  )
}

export const PendingAlias: React.FC<{ alias: string }> = ({ alias }) => (
  <Chip
    label={alias.split('@')[0]}
    size="small"
    variant="outlined"
    onDelete={() => {}}
    deleteIcon={<CircularProgress size={12} />}
    sx={{ opacity: 0.6, cursor: 'default' }}
  />
)

const today = new Date().toISOString().split('T')[0]

interface AliasProps {
  alias: AliasData
  search: string
  deletingAlias?: string
  onDeleteAlias: (alias: string) => void
  onAliasUpdated: (alias: AliasData) => void
  onToggleAlias: (alias: AliasData) => void
}

export const Alias: React.FC<AliasProps> = ({
  alias,
  search,
  deletingAlias,
  onDeleteAlias,
  onAliasUpdated,
  onToggleAlias
}) => {
  const navigate = useNavigate()
  const [menuAnchor, setMenuAnchor] = useState<null | HTMLElement>(null)
  const [_copied, setCopied] = useState(false)
  const [editOpen, setEditOpen] = useState(false)

  const isDeleting = deletingAlias === alias.alias
  const isExpired: boolean = alias.expires !== '' && alias.expires < today
  const isDisabled = isAliasDisabled(alias.alias)
  const displayEmail = aliasDisplayEmail(alias.alias)

  const handleCopy = (): void => {
    void navigator.clipboard.writeText(alias.alias)
    setCopied(true)
  }

  const handleMenuOpen = (e: React.MouseEvent<HTMLElement>): void => {
    e.stopPropagation()
    setMenuAnchor(e.currentTarget)
  }

  const handleMenuClose = (): void => {
    setMenuAnchor(null)
  }

  const handleMove = (): void => {
    handleMenuClose()
    navigate(`${AliasMove}?alias=${alias.alias}${window.location.hash}`)
  }

  const handleEdit = (): void => {
    handleMenuClose()
    setEditOpen(true)
  }

  const handleDelete = (): void => {
    handleMenuClose()
    onDeleteAlias(alias.alias)
  }

  const handleToggle = (): void => {
    handleMenuClose()
    onToggleAlias(alias)
  }

  const chipLabel = (
    <Box sx={{ display: 'inline-flex', alignItems: 'center', gap: 0.25 }}>
      {isDisabled && (
        <DisabledAliasIcon
          sx={{ fontSize: '0.75rem', verticalAlign: 'middle' }}
        />
      )}
      <Address value={displayEmail} search={search} showDomain={false} />
      {alias.expires !== '' && (
        <Typography
          component="span"
          variant="caption"
          sx={{ color: isExpired ? 'warning.main' : 'text.secondary' }}
        >
          &nbsp;# {alias.expires}
        </Typography>
      )}
      {isExpired && !isDeleting && (
        <>
          <Box
            component="span"
            role="button"
            title="Reschedule"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleEdit()
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'warning.main',
              cursor: 'pointer',
              '&:hover': { opacity: 0.7 }
            }}
          >
            <RescheduleIcon sx={{ fontSize: '0.85rem' }} />
          </Box>
          <Box
            component="span"
            role="button"
            title="Delete alias"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation()
              handleDelete()
            }}
            sx={{
              display: 'inline-flex',
              alignItems: 'center',
              color: 'warning.main',
              cursor: 'pointer',
              '&:hover': { opacity: 0.7 }
            }}
          >
            <PermanentDeleteIcon sx={{ fontSize: '0.85rem' }} />
          </Box>
        </>
      )}
    </Box>
  )

  const tooltipContent =
    alias.description !== '' || alias.expires !== '' ? (
      <Box>
        {alias.description !== '' && (
          <Typography variant="caption" display="block">
            {alias.description}
          </Typography>
        )}
        {alias.expires !== '' && (
          <Typography variant="caption" display="block" sx={{ opacity: 0.8 }}>
            {isExpired ? 'Expired' : 'Expires'}: {alias.expires}
          </Typography>
        )}
      </Box>
    ) : (
      ''
    )

  return (
    <>
      <Tooltip title={tooltipContent} placement="top" arrow>
        <Chip
          label={chipLabel}
          onClick={isDeleting ? undefined : handleCopy}
          onDelete={isDeleting ? () => {} : handleMenuOpen}
          deleteIcon={
            isDeleting ? (
              <CircularProgress size={12} color="inherit" />
            ) : (
              <AliasMenuIcon fontSize="small" />
            )
          }
          size="small"
          variant="outlined"
          sx={{
            cursor: isDeleting ? 'default' : 'pointer',
            textDecoration: isDeleting ? 'line-through' : 'none',
            opacity: isDisabled && !isDeleting ? 0.55 : 1,
            color: isDeleting
              ? 'text.disabled'
              : isExpired
                ? 'warning.main'
                : 'inherit',
            borderColor: isDeleting
              ? 'divider'
              : isExpired
                ? 'warning.main'
                : isDisabled
                  ? 'text.disabled'
                  : 'inherit',
            borderStyle: isDisabled && !isDeleting ? 'dashed' : 'solid'
          }}
        />
      </Tooltip>
      <Menu
        anchorEl={menuAnchor}
        open={Boolean(menuAnchor)}
        onClose={handleMenuClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        transformOrigin={{ vertical: 'top', horizontal: 'right' }}
      >
        <MenuItem onClick={handleEdit} dense>
          Edit
        </MenuItem>
        <MenuItem onClick={handleMove} dense>
          Move
        </MenuItem>
        <MenuItem onClick={handleToggle} dense>
          {isDisabled ? 'Enable' : 'Disable'}
        </MenuItem>
        <MenuItem onClick={handleDelete} dense sx={{ color: 'error.main' }}>
          Delete
        </MenuItem>
      </Menu>
      {editOpen && (
        <AliasEdit
          alias={alias}
          open={editOpen}
          onClose={() => {
            setEditOpen(false)
          }}
          onSaved={(updated) => {
            setEditOpen(false)
            onAliasUpdated(updated)
          }}
        />
      )}
    </>
  )
}
